/**
 * The implant campaign's outcome vocabulary — ONE list, two homes.
 *
 * Tab 1 (รายชื่อโทร) writes it as a PREFIX inside `recall.Note`:
 *     [IMP|C|PR] คุยแล้ว ราคาสูงไป ขอคิดดูก่อน
 * Tab 2's manual layer (v1.5) will write the same codes onto a `ticket`.
 *
 * WHY A PREFIX AND NOT A COLUMN. `recall` (table 47) has only
 * created_by / success / Note / date / patient_id — no campaign column, no
 * outcome column, no objection column (implant/project.md §5 gap 6). Adding one
 * is a schema change on a live table; a machine-readable prefix is not. The
 * grammar is fixed so a reader can parse it later:
 *
 *     [IMP|<list>|<code>] free text
 *      ^^^  ^^^^^^ ^^^^^^
 *      |    |      one of CODES below
 *      |    A B C D E F G  (which list the row was called from)
 *      always literal
 *
 * 🔑 THE CHIPS ARE THE POINT. Nobody types an 11-character prefix ninety times a
 * week. P9 — the share of recall rows that do NOT start `[IMP|`, target ≤5% —
 * runs at ~40% without a one-tap control, and then the whole campaign is
 * unmeasurable. See RecallNotesSection's `quickCodes` prop.
 *
 * ⚠️ NO AND OC ARE LOAD-BEARING, NOT LABELS (Ram, 2026-09-17). Xano api 650's
 * `excl` CTE hides any patient whose recall Note matches, anchored:
 *     left("Note",5) = '[IMP|' AND split_part("Note",'|',3) LIKE 'NO]%'  (or OC]%)
 * so `[IMP|C|NO]` เคสไม่เหมาะ or `[IMP|C|OC]` ไปทำที่อื่นแล้ว takes that patient
 * off EVERY implant list on the next fetch — whoever wrote it. Since api 678
 * (2026-10-29) that is no longer permanent: the patient is parked in view R
 * (พักจนกว่าจะมีรอบใหม่) until a new extraction or a new implant consult starts a
 * round. Their chips and pills wear their own tone, `park` (red + the hourglass
 * that marks view R), so a caller sees which taps silence a patient before tapping.
 * IMPLANT-ONLY: regular recall never reads notes. Undo = delete the note.
 * Renaming either code without changing that SQL silently stops the exclusion.
 * Change one, change the other — EXCLUDING_CODES below is the list to keep in step.
 *
 * ⛔ DNC IS BACK AS ยกเลิกเคส, 2026-10-29 (retired 2026-09-17 as ห้ามติดต่ออีก). The
 * clinic has ONE do-not-recall switch, patient.status = 'ยกเลิกเคส' — regular recall
 * and api 678 both obey it. DNC was retired because it was a SECOND, implant-only
 * "do not contact" the rest of the clinic could not see. It returns only because
 * the chip now WRITES that one switch: saving it (after a ConfirmDialog) sets
 * patient.status = ยกเลิกเคส through POST /api/patients/status/{id}, then stores the
 * note as the record of who and why (`cancels` below; RecallNotesSection does it).
 * The exclusion is the status, not the note — deleting the note restores nothing.
 * Same code, so api 678 needs no change (it already parks DNC in view R, and the
 * status hides the patient everywhere anyway).
 *
 * THE SET, 2026-10-29 (Ram): five groups, one colour each — จองแล้ว (green) ·
 * ติดต่อได้ CN PR FE (amber) · ไม่รับสาย NA (grey) · พักจนถอนฟันซี่ใหม่ OC NO (blue) ·
 * ยกเลิกเคส DNC (red, the colour of the ยกเลิกเคส status pill). TM FM NU IV are
 * retired: no chip, but an old note still reads as words, and api 678 still parks
 * TM FM NU for 6 months.
 *
 * ⚠️ AND `recall.success` IS UNUSABLE — do not read it as "booked". The Xano
 * upsert recomputes it from ANY matching appointment, and every name on every
 * list is an existing patient who already comes in for scaling, so it flips true
 * on a routine cleaning. "Booked" is BK here, and measured properly it is an
 * appointment with appointmentlist_id IN (35,83,84) created after the patient's
 * first [IMP| recall — measured on created_at, never the client-supplied
 * recall.date.
 *
 * plan.md §6.3 says this list gets rewritten on a Monday from what Pan actually
 * hears in her first four days. That rewrite has to be ONE edit — this file.
 *
 * 👁 THE PREFIX IS FOR MACHINES, NEVER FOR A READER (2026-09-17). It used to be
 * seeded into the note box and printed raw in every note list, so staff read
 * `[IMP|A|OC]` instead of ไปทำที่อื่นแล้ว, could type over it (a damaged DNC
 * silently stops suppressing), and lost their text when tapping a second chip.
 * Now the chip is a selection, the prefix is attached on save by codedNote(), and
 * every reader goes through splitNote() → components/recall/RecallNoteText.jsx,
 * which renders it as a labelled pill. THE STORED FORMAT DID NOT CHANGE — api 650
 * and every note already written read exactly as before.
 */

// tone → the pill classes used by the chips. Kept local so the vocabulary file
// has no dependency on a component. One tone per group (see THE SET above).
export const OUTCOME_TONES = {
  good:    'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
  warm:    'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
  cold:    'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100',
  park:    'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100',     // OC NO: พัก (freeze) จนรอบใหม่
  cancel:  'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',     // DNC: ยกเลิกเคส, as its status pill
}

// The groups, in the order the chips read (Ram, 2026-10-29). `key` is the one-line
// legend under the chips; only the groups whose effect is not obvious carry one.
export const OUTCOME_GROUPS = [
  { id: 'booked',  tone: 'good',   label: 'จองแล้ว' },
  { id: 'contact', tone: 'warm',   label: 'ติดต่อได้' },
  { id: 'noreply', tone: 'cold',   label: 'ติดต่อได้แต่ไม่รับสาย' },
  { id: 'freeze',  tone: 'park',   label: 'พักจนถอนฟันซี่ใหม่',
    key: 'พักคนไข้จนกว่าจะถอนฟันซี่ใหม่ หรือมีนัดปรึกษารากเทียมใหม่' },
  { id: 'cancel',  tone: 'cancel', label: 'ยกเลิกเคส',
    key: 'นำออกจาก recall ทุกประเภทถาวร และเปลี่ยนสถานะคนไข้เป็น “ยกเลิกเคส”' },
]

export const OUTCOME_CODES = [
  { code: 'BK',  thai: 'จองแล้ว',              group: 'booked',  tone: 'good' },
  { code: 'CN',  thai: 'คุยแล้ว ยังไม่ตัดสินใจ', group: 'contact', tone: 'warm' },
  { code: 'PR',  thai: 'ติดเรื่องราคา',          group: 'contact', tone: 'warm' },
  { code: 'FE',  thai: 'กลัว/กังวล',            group: 'contact', tone: 'warm' },
  { code: 'NA',  thai: 'ไม่รับสาย',             group: 'noreply', tone: 'cold' },
  { code: 'OC',  thai: 'ไปทำที่อื่นแล้ว',         group: 'freeze',  tone: 'park', excludes: true },
  { code: 'NO',  thai: 'เคสไม่เหมาะ',            group: 'freeze',  tone: 'park', excludes: true },
  { code: 'DNC', thai: 'ยกเลิกเคส',             group: 'cancel',  tone: 'cancel', cancels: true },
  // retired 2026-10-29 — no chip; an old note still reads as words. See header.
  { code: 'TM',  thai: 'ยังไม่มีเวลา',           tone: 'warm', retired: true },
  { code: 'FM',  thai: 'ขอถามที่บ้านก่อน',       tone: 'warm', retired: true },
  { code: 'NU',  thai: 'ยังไม่เดือดร้อน',         tone: 'warm', retired: true },
  { code: 'IV',  thai: 'เบอร์เสีย/ติดต่อไม่ได้',   tone: 'cold', retired: true },
]

/** The patient.status the ยกเลิกเคส chip writes — the clinic's one do-not-recall switch. */
export const CANCEL_STATUS = 'ยกเลิกเคส'

/** Codes that park a patient until a new round (api 678 view R). Keep in step with
 *  api 678. DNC parks there too, but it is the status that keeps that patient out. */
export const EXCLUDING_CODES = OUTCOME_CODES.filter((o) => o.excludes).map((o) => o.code)

/** Said under the box the moment an excluding chip is picked, before anything is saved. */
export const EXCLUDE_WARNING = 'ผลนี้จะพักคนไข้ไว้จนกว่าจะถอนฟันซี่ใหม่หรือมาปรึกษารากเทียมใหม่ — ไม่ขึ้นในรายชื่อโทรรากเทียมทุกลิสต์จนถึงตอนนั้น · recall ปกติยังโทรได้ตามเดิม · ถ้าบันทึกผิดให้ลบโน้ตนี้'

export const OUTCOME_BY_CODE = Object.fromEntries(OUTCOME_CODES.map((o) => [o.code, o]))

/** The prefix a note from `list` must start with. Keep in step with api 650. */
export const RECALL_PREFIX = (list) => `[IMP|${String(list || '').toUpperCase()}|`

/** Seed text for the note box when a chip is tapped: `[IMP|C|PR] `. */
export const seedNote = (list, code) => `${RECALL_PREFIX(list)}${code}] `

/**
 * Parse a note back into {list, code}. Returns null when the note does not carry
 * the prefix — which is the P9 measurement, so a null here is a real signal, not
 * an error. Mirrors the SQL: anchored at position 0, third `|`-segment.
 */
export function parseNote(note) {
  const s = String(note || '')
  if (s.slice(0, 5) !== '[IMP|') return null
  const parts = s.split('|')
  if (parts.length < 3) return null
  const list = parts[1]
  const close = parts[2].indexOf(']')
  if (close < 0) return null
  const code = parts[2].slice(0, close)
  if (!list || !code) return null
  return { list, code }
}

/**
 * The Note to store for a coded call: `[IMP|C|PR] ราคาสูงไป`, or just `[IMP|E|DNC]`
 * when nothing was typed. The one place a coded Note is assembled — the add box
 * and the edit box both call it, so the prefix api 650 keys DNC on can never be
 * typed over by hand.
 */
export const codedNote = (list, code, text = '') =>
  `${seedNote(list, code)}${String(text ?? '').trim()}`.trim()

/**
 * A stored Note split for a reader: `{ list, code, outcome, text }` for a coded
 * note (`outcome` is null for a code this file no longer knows), and
 * `{ list: null, code: null, outcome: null, text }` for anything else. Built on
 * parseNote, so it is anchored exactly like the SQL — a DNC mentioned mid-sentence
 * stays plain text.
 */
export function splitNote(note) {
  const s = String(note ?? '')
  const p = parseNote(s)
  if (!p) return { list: null, code: null, outcome: null, text: s.trim() }
  const head = '[IMP|'.length + p.list.length + 1 + p.code.length + 1   // … "|" code "]"
  return { ...p, outcome: OUTCOME_BY_CODE[p.code] || null, text: s.slice(head).trim() }
}

/** The outcome's Thai label, falling back to the raw code rather than hiding it. */
export const outcomeLabel = (n) => n?.outcome?.thai || n?.code || ''

/** One line of plain text, for places that cannot hold a pill (a title tooltip). */
export function noteLabel(note) {
  const n = splitNote(note)
  if (!n.code) return n.text
  const head = `รากเทียม · ${outcomeLabel(n)}`
  return n.text ? `${head} — ${n.text}` : head
}

/** Pill classes for a stored note — the chip tone without its hover state. */
export const outcomePillCls = (tone) =>
  (OUTCOME_TONES[tone] || OUTCOME_TONES.cold).replace(/\s*hover:\S+/g, '')

/** Chips for the note box, in group order (OUTCOME_CODES is written in it). */
export const quickCodesFor = (list) => OUTCOME_CODES.filter((o) => !o.retired).map((o) => ({
  ...o,
  seed: seedNote(list, o.code),
  cls: OUTCOME_TONES[o.tone] || OUTCOME_TONES.cold,
}))
